import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Modal, Table, Tag, Button, Tooltip, Typography, message } from 'antd'
import { LinkOutlined, DownloadOutlined } from '@ant-design/icons'
import api from '../../../../services/api'

const { Text } = Typography

const PAGE_SIZE = 10

const STATUS_CONFIG = {
  succeeded: { color: '#52c41a', label: 'Success' },
  pending: { color: '#fa8c16', label: 'Pending' },
  failed: { color: '#ff4d4f', label: 'Failed' },
}

const formatDate = (timestamp) => {
  if (!timestamp) return '—'
  const d = new Date(timestamp * 1000)
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  })
}

const formatCurrency = (amount) => {
  if (amount == null) return '—'
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(amount)
}

const COLUMNS = [
  {
    title: 'Date & Time',
    dataIndex: 'transaction_date',
    key: 'transaction_date',
    width: 170,
    render: (val) => (
      <Text style={{ fontSize: 13 }}>{formatDate(val)}</Text>
    )
  },
  {
    title: 'Credits',
    dataIndex: 'credits',
    key: 'credits',
    width: 120,
    align: 'right',
    render: (val) => (
      <Text strong style={{ color: '#1890ff' }}>
        {val != null ? `+${val.toLocaleString()}` : '—'}
      </Text>
    )
  },
  {
    title: 'Amount Paid',
    dataIndex: 'amount',
    key: 'amount',
    width: 120,
    align: 'right',
    render: (val) => (
      <Text strong>{formatCurrency(val)}</Text>
    )
  },
  {
    title: 'Payment Method',
    dataIndex: 'payment_method',
    key: 'payment_method',
    width: 150,
    render: (val) => {
      if (!val) return <Text type="secondary">—</Text>
      const display = val.charAt(0).toUpperCase() + val.slice(1)
      return <Text>{display}</Text>
    }
  },
  {
    title: 'Status',
    dataIndex: 'transaction_status',
    key: 'transaction_status',
    width: 110,
    align: 'center',
    render: (val) => {
      const key = val && val.toLowerCase()
      const cfg = STATUS_CONFIG[key] || { color: 'default', label: val || '—' }
      return <Tag color={cfg.color}>{cfg.label}</Tag>
    }
  },
  {
    title: 'Receipt',
    dataIndex: 'receipt_url',
    key: 'receipt_url',
    width: 80,
    align: 'center',
    render: (url) =>
      url ? (
        <Tooltip title="View receipt">
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </Button>
        </Tooltip>
      ) : (
        <Text type="secondary">—</Text>
      )
  }
]

const PurchaseHistoryModal = ({ visible, onClose }) => {
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const fetchHistory = useCallback(async (currentPage) => {
    setLoading(true)
    try {
      const offset = (currentPage - 1) * PAGE_SIZE
      const res = await api.aiCredits.getPurchaseHistory({ offset, limit: PAGE_SIZE })
      if (res && res.data) {
        setData(res.data.result || [])
        setTotal(res.data.total || 0)
      } else {
        throw new Error('Failed to load purchase history')
      }
    } catch (err) {
      message.error(err.message || 'Failed to load purchase history')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (visible) {
      setPage(1)
      fetchHistory(1)
    }
  }, [visible, fetchHistory])

  const handlePageChange = (newPage) => {
    setPage(newPage)
    fetchHistory(newPage)
  }

  const handleClose = () => {
    setData([])
    setTotal(0)
    setPage(1)
    if (onClose) onClose()
  }

  const handleExport = () => {
    if (!data || data.length === 0) {
      message.warning('No data to export.')
      return
    }
    const headers = ['Date & Time', 'Credits', 'Amount Paid (USD)', 'Payment Method', 'Status', 'Receipt URL']
    const rows = data.map(function (row) {
      return [
        formatDate(row.transaction_date),
        row.credits != null ? row.credits : '',
        row.amount != null ? row.amount : '',
        row.payment_method || '',
        row.transaction_status || '',
        row.receipt_url || ''
      ].map(function (cell) {
        return '"' + String(cell).replace(/"/g, '""') + '"'
      }).join(',')
    })
    const csv = [headers.join(',')].concat(rows).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'purchase_history.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Purchase History</span>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            type="default"
            style={{ color: '#1890ff', borderColor: '#1890ff' }}
          >
            Export History
          </Button>
        </div>
      }
      visible={visible}
      onCancel={handleClose}
      width={900}
      footer={null}
      destroyOnClose
    >
      <Table
        columns={COLUMNS}
        dataSource={data}
        rowKey={(row, i) => `${row.transaction_date}-${i}`}
        loading={loading}
        scroll={{ x: 700 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: handlePageChange,
          showSizeChanger: false,
          showTotal: (t) => `${t} transaction${t !== 1 ? 's' : ''}`,
          size: 'small'
        }}
        size="middle"
        locale={{ emptyText: 'No purchase history found.' }}
      />
    </Modal>
  )
}

export default PurchaseHistoryModal
